import React, {useCallback, useEffect, useRef, useState} from "react";
import {Badge, Button, Card, Col, Row} from "react-bootstrap";
import Modals from "../../../components/modals";
import axios from "../../../utils/apiClient";
import {useLocation} from "react-router-dom";
import SubmitRecentVisitBody from "./../SubmitRecentVisit";
import ScheduledAppointment from "./modals/scheduleAppointment";
import NoShowModal from "./modals/noShow";
import {ROUTES} from '../../../utils/navigationItems';
import PatientRow from "./PatientRow";
import {useDispatch, useSelector} from "react-redux";
import {setReloadPatientList} from "../../../actions/manageVisitActions";

import {formatDateWithPattern} from "../../../utils/util";
import {format} from "date-fns";

const componentName = "ScheduleCardView";

const buildVisitGapsUrl = (patientId, action, careGapId) => {
    const baseUrl = process.env.REACT_APP_REST_API_BASE_URL;
    const params = [
        `patientId=${encodeURIComponent(patientId ?? "")}`,
        `action=${encodeURIComponent(action ?? "")}`,
    ];

    if (careGapId !== undefined) {
        params.push(`careGapId=${encodeURIComponent(careGapId ?? "null")}`);
    }

    return `${baseUrl}/v1/patients/getVisitGaps?${params.join("&")}`;
};

const buildUpdateVisitGapsUrl = (patientId, action, careGapId) => {
    const baseUrl = process.env.REACT_APP_REST_API_BASE_URL;
    const params = [
        `patientId=${encodeURIComponent(patientId ?? "")}`,
        `action=${encodeURIComponent(action ?? "")}`,
    ];

    if (careGapId !== undefined) {
        params.push(`careGapId=${encodeURIComponent(careGapId ?? "null")}`);
    }

    return `${baseUrl}/v1/patients/updateVisitGaps?${params.join("&")}`;
};

const resolveCareGapId = (patient) => {
    if (!patient) return null;

    const addCandidate = (collection, value) => {
        if (value === undefined || value === null) return;
        const normalized = typeof value === "string" ? value.trim() : value;
        if (normalized !== "" && normalized !== null) {
            collection.push(normalized);
        }
    };

    const candidates = [];

    addCandidate(candidates, patient.nextVisitId);
    addCandidate(candidates, patient.nextVisitID);
    addCandidate(candidates, patient.nextVisitGapId);
    addCandidate(candidates, patient.nextVisitGapID);

    addCandidate(candidates, patient.careGapId);
    addCandidate(candidates, patient.careGapID);
    addCandidate(candidates, patient.patientGapId);
    addCandidate(candidates, patient.patientGapID);

    if (Array.isArray(patient.careGapIds) && patient.careGapIds.length) {
        addCandidate(candidates, patient.careGapIds[0]);
    } else if (typeof patient.careGapIds === "string" && patient.careGapIds.length) {
        addCandidate(candidates, patient.careGapIds.split(",")[0]);
    }

    if (Array.isArray(patient.patientGapIds) && patient.patientGapIds.length) {
        addCandidate(candidates, patient.patientGapIds[0]);
    } else if (typeof patient.patientGapIds === "string" && patient.patientGapIds.length) {
        addCandidate(candidates, patient.patientGapIds.split(",")[0]);
    }

    return candidates.find((id) => id !== undefined && id !== null && id !== "") || null;
};

const toDateKey = (value) => {
    if (!value) return null;
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed.length >= 10) {
            const possible = trimmed.slice(0, 10);
            if (/^\d{4}-\d{2}-\d{2}$/.test(possible)) {
                return possible;
            }
        }
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return format(date, "yyyy-MM-dd");
};

const resolveVisitAppointmentDate = (visit) => {
    if (!visit) return null;
    return (
        visit.appointmentDate ||
        visit.patientGapAppointmentDate ||
        visit.visitDate ||
        visit.appointmentDateTime ||
        null
    );
};

const ScheduleListView = ({
                      patientList,
                      quickListType,
                      handleChangeCheckBox,
                      handleAllPatientsCheckbox,
                      isPatientSelected,
                      updateParent,
                      closeEvidenceModal,
                      handleFilterChange,
                      filterParams,
                      pageNumber,
                      print,
                      handleFilterDatesResult
                  }) => {
    const isEmpty = require("is-empty");
    const dispatch = useDispatch();
    const todayRef = useRef(null);

    const [, setShowEvidenceModal] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState({});
    const [confirmationMessage, setConfirmationMessage] = useState(false);
    const [showRecentVisitModal, setShowRecentVisitModal] = useState(false);
    const [showPatientDetails, setShowPatientDetails] = useState(false);
    const [activeAction, setActiveAction] = useState(null);
    const [noShowVisits, setNoShowVisits] = useState([]);
    const [noShowPrimaryCareGapId, setNoShowPrimaryCareGapId] = useState(null);
    const [noShowSelectedCareGapIds, setNoShowSelectedCareGapIds] = useState([]);
    const [noShowLoading, setNoShowLoading] = useState(false);
    const [noShowSubmitting, setNoShowSubmitting] = useState(false);
    const [noShowError, setNoShowError] = useState(null);
    const [noShowSubmitError, setNoShowSubmitError] = useState(null);
    const [showNoShowModal, setShowNoShowModal] = useState(false);

    const resetNoShowState = useCallback(() => {
        setNoShowVisits([]);
        setNoShowPrimaryCareGapId(null);
        setNoShowSelectedCareGapIds([]);
        setNoShowError(null);
        setNoShowSubmitError(null);
        setNoShowLoading(false);
        setNoShowSubmitting(false);
    }, []);

    const loadNoShowVisits = useCallback(async (patient) => {
        if (!patient?.id) {
            return;
        }

        const primaryCareGapId = resolveCareGapId(patient);
        const normalizedPrimaryCareGapId =
            primaryCareGapId === undefined || primaryCareGapId === null
                ? null
                : String(primaryCareGapId).trim();
        setNoShowPrimaryCareGapId(primaryCareGapId);
        setNoShowError(null);
        setNoShowSubmitError(null);
        setNoShowLoading(true);

        const appointmentDateKey = toDateKey(
            patient?.patientGapAppointmentDate ||
            patient?.appointmentDate ||
            patient?.appointmentDateTime ||
            patient?.nextVisit ||
            null
        );

        const fallbackVisits =
            primaryCareGapId
                ? [
                    {
                        careGapId: primaryCareGapId,
                        description: patient?.displayName || patient?.gapDescription || "Scheduled visit",
                        appointmentDate:
                            patient?.patientGapAppointmentDate ||
                            patient?.appointmentDate ||
                            patient?.appointmentDateTime ||
                            null,
                    },
                ]
                : [];

        const primeSelection = (visits) => {
            const normalizedVisits = Array.isArray(visits) ? visits : [];
            setNoShowVisits(normalizedVisits);
            const preferredVisit =
                normalizedVisits.find((visit) => {
                    if (!visit?.careGapId || !normalizedPrimaryCareGapId) return false;
                    return String(visit.careGapId).trim() === normalizedPrimaryCareGapId;
                }) ||
                normalizedVisits.find((visit) => !!visit?.careGapId) ||
                null;
            setNoShowSelectedCareGapIds(
                preferredVisit?.careGapId ? [preferredVisit.careGapId] : []
            );
        };

        try {
            const url = buildVisitGapsUrl(patient.id, "NO_SHOW", primaryCareGapId);

            const response = await axios.get(url, {
                headers: { "Content-Type": "application/json" },
            });

            const rawSameDay = Array.isArray(response?.data?.sameDayVisits)
                ? response.data.sameDayVisits
                : [];
            const otherVisits = [
                ...(Array.isArray(response?.data?.dueNowVisits) ? response.data.dueNowVisits : []),
                ...(Array.isArray(response?.data?.dueSoonVisits) ? response.data.dueSoonVisits : []),
                ...(Array.isArray(response?.data?.scheduledVisits) ? response.data.scheduledVisits : []),
            ];

            let candidateVisits = rawSameDay.length ? rawSameDay : otherVisits;

            if (appointmentDateKey && candidateVisits.length) {
                const sameDayMatches = candidateVisits.filter((visit) => {
                    const visitDateKey = toDateKey(resolveVisitAppointmentDate(visit));
                    return visitDateKey ? visitDateKey === appointmentDateKey : false;
                });
                if (sameDayMatches.length) {
                    candidateVisits = sameDayMatches;
                }
            }

            const uniqueVisits = [];
            const trackedIds = new Set();

            candidateVisits.forEach((visit) => {
                if (!visit) return;
                const careGapId =
                    visit.careGapId ??
                    visit.patientGapId ??
                    visit.patientGapID ??
                    visit.careGapID ??
                    null;

                if (!careGapId || trackedIds.has(careGapId)) return;
                trackedIds.add(careGapId);
                uniqueVisits.push({
                    ...visit,
                    careGapId,
                });
            });

            primeSelection(uniqueVisits.length ? uniqueVisits : fallbackVisits);
        } catch (error) {
            console.error("Failed to load no-show visits:", error);
            const message =
                error?.response?.data?.message || error?.message || "Unable to load scheduled visits.";
            setNoShowError(message);
            primeSelection(fallbackVisits);
        } finally {
            setNoShowLoading(false);
        }
    }, []);

    const updateNoShowSelection = useCallback(
        (careGapId, isSelected) => {
            if (!careGapId) return;
            setNoShowSubmitError(null);
            setNoShowSelectedCareGapIds((prev) => {
                if (isSelected) {
                    if (prev.includes(careGapId)) {
                        return prev;
                    }
                    return [...prev, careGapId];
                }
                return prev.filter((id) => id !== careGapId);
            });
        },
        [setNoShowSelectedCareGapIds, setNoShowSubmitError]
    );

    const handleSubmitNoShow = useCallback(async () => {
        if (noShowLoading || noShowSubmitting) return;

        const patientId = selectedPatient?.id;
        if (!patientId) {
            setNoShowSubmitError("Patient information is missing.");
            return;
        }

        const primaryCareGapId = noShowPrimaryCareGapId || resolveCareGapId(selectedPatient);
        const visitsWithIds = noShowVisits.filter((visit) => !!visit?.careGapId);
        const hasMultipleVisits = noShowVisits.length > 1;

        let visitsToUpdate = [];

        if (visitsWithIds.length) {
            const visitsById = new Map(
                visitsWithIds.map((visit) => [visit.careGapId, visit])
            );

            if (hasMultipleVisits) {
                const selected = noShowSelectedCareGapIds.filter((id) => visitsById.has(id));
                if (!selected.length) {
                    setNoShowSubmitError("Select at least one visit to update.");
                    return;
                }
                visitsToUpdate = selected.map((id) => visitsById.get(id)).filter(Boolean);
            } else {
                const fallbackVisit =
                    visitsById.get(noShowSelectedCareGapIds[0]) ||
                    visitsById.get(primaryCareGapId) ||
                    visitsWithIds[0] ||
                    null;
                if (fallbackVisit) {
                    visitsToUpdate = [fallbackVisit];
                }
            }
        }

        if (!visitsToUpdate.length) {
            if (primaryCareGapId) {
                visitsToUpdate = [
                    {
                        careGapId: primaryCareGapId,
                        appointmentDate:
                            selectedPatient?.patientGapAppointmentDate ||
                            selectedPatient?.appointmentDate ||
                            selectedPatient?.appointmentDateTime ||
                            null,
                    },
                ];
            } else {
                setNoShowSubmitError("No visits available to update.");
                return;
            }
        }

        const payload = visitsToUpdate.map((visit) => ({
            careGapId: visit.careGapId,
            statusEnum: null,
            appointmentDate:
                resolveVisitAppointmentDate(visit) ||
                selectedPatient?.patientGapAppointmentDate ||
                selectedPatient?.appointmentDate ||
                selectedPatient?.appointmentDateTime ||
                null,
        }));

        try {
            setNoShowSubmitting(true);
            setNoShowSubmitError(null);

            const url = buildUpdateVisitGapsUrl(
                patientId,
                "NO_SHOW",
                primaryCareGapId ?? payload[0]?.careGapId ?? null
            );

            await axios.post(url, payload, {
                headers: { "Content-Type": "application/json" },
            });

            if (typeof handleFilterDatesResult === "function") {
                await handleFilterDatesResult(pageNumber);
            }

            resetNoShowState();
            setShowNoShowModal(false);
            setActiveAction(null);
        } catch (error) {
            console.error("Failed to update no-show visits:", error);
            const message =
                error?.response?.data?.message ||
                error?.message ||
                "Unable to update visits. Please try again.";
            setNoShowSubmitError(message);
        } finally {
            setNoShowSubmitting(false);
        }
    }, [
        noShowLoading,
        noShowSubmitting,
        selectedPatient,
        noShowPrimaryCareGapId,
        noShowVisits,
        noShowSelectedCareGapIds,
        handleFilterDatesResult,
        pageNumber,
        resetNoShowState,
        setShowNoShowModal,
    ]);

    const openAction = useCallback((action, patient) => {
        setSelectedPatient(patient);
        setActiveAction(action);

        if (action === "noshow") {
            resetNoShowState();
            setShowNoShowModal(true);
            loadNoShowVisits(patient);
        } else {
            setShowNoShowModal(false);
        }
    }, [loadNoShowVisits, resetNoShowState, setShowNoShowModal]);

    const closeAction = useCallback(() => {
        if (activeAction === "noshow") {
            resetNoShowState();
            setShowNoShowModal(false);
        }
        setActiveAction(null);
    }, [activeAction, resetNoShowState, setShowNoShowModal]);

    const visitPatient = useSelector((state) => state?.manageVisitReducer?.patient);
    const reloadPatients = useSelector((state) => state?.manageVisitReducer?.reload);

    useEffect(() => {
        if (reloadPatients) {
            handleFilterChange(filterParams, pageNumber);
            dispatch(setReloadPatientList(false))
        }
    }, [dispatch, filterParams, handleFilterChange, pageNumber, reloadPatients])

    const closeEvidenceModalMain = () => {
        setConfirmationMessage(false);
        setShowEvidenceModal(false);
        closeEvidenceModal();
    };

    const printHere = (pts) => {
        if(pts && pts.length){
        print(pts.map(e=>e.id));
        }
       
    };

    const quickListState = useLocation();
    let pageTitle = quickListState.state ? quickListState.state.pageTitle : '';
    if (!pageTitle) {
        const pathName = quickListState.pathname ? quickListState.pathname : '';
        Object.entries(ROUTES).forEach(([key, value]) => {
            if (value && pathName && value.path && value.path.split("/").join("") === pathName.split("/").join("")) {
                pageTitle = key;
            }
        });
    }

    useEffect(() => {
        if (visitPatient?.id) {
            setSelectedPatient(visitPatient)
            setShowPatientDetails(true)
        }
    }, [visitPatient])

    function getDay(date) {
        const daysOfWeek = [ 'Sunday', 'Monday', "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
        const dayIndex = daysOfWeek[date.getDay()];
        return dayIndex;
    }

    function formatDate(dateString) {
        const today = new Date();
        const tomorrow = new Date();
        const date = new Date(dateString);
        date.setTime(date.getTime() + date.getTimezoneOffset() * 60 * 1000);
        tomorrow.setDate(today.getDate() + 1);
        if(date.toDateString() === today.toDateString()){
            return 'Today';
        }
        else if(date.toDateString() === tomorrow.toDateString()){
            return 'Tomorrow';
        }
        else {
            return getDay(date);
        }
    }

    function scrollPatientList(dateString) {
        const date = new Date(dateString);
        date.setTime(date.getTime() + date.getTimezoneOffset() * 60 * 1000);
        if (date.toDateString() === new Date().toDateString()) {
            return todayRef;
        }
        return null;
    }

    useEffect(() => { // Automatically scroll to today's element after the data is loaded
        if (todayRef.current) {
            todayRef.current.scrollIntoView({behavior: 'smooth'});
        }
    }, [patientList]);

    return (
        <Row className="container-fluid p-0 m-0">
            <Col className="p-0">
                {Object.keys(patientList).map((key, index) => {
                    const patientGroup = patientList[key];
                    const groupKey = patientGroup.date ?? key;

                    return (
                        <React.Fragment key={`${componentName}-patientsGroup-${groupKey}`}>
                            {!isEmpty(patientGroup.data) ? (
                              <Row id={`${componentName}-patientsSection-${index}`} className="mb-5">
                                  <Col>
                                      {!isEmpty(patientGroup.data) &&
                                          <div className="d-flex flex-row justify-content-between align-items-center mb-2"  ref={scrollPatientList(patientGroup.date)}>
                                              <div className="d-flex flex-row align-items-center">
                                                  <div className="d-flex flex-column align-items-start">
                                                      <small id={`${componentName}-MonthandDay-${index}`}>{formatDateWithPattern(patientGroup.date, "MMMM d")}</small>
                                                      <h5 className="mb-0">
                                                          <span id={`${componentName}-DayoftheWeek-${index}`} className="me-2">{formatDate(patientGroup.date)}</span>
                                                          <Badge pill id={`${componentName}-PatientCount-${index}`} className="bg-primary">
                                                              {patientGroup.totalSize.toLocaleString()}
                                                          </Badge>
                                                      </h5>
                                                  </div>
                                              </div>
                                               <Button variant="secondary" id={`${componentName}-patientChartPrepTool`} onClick={()=>printHere(patientGroup.data)} className="border">
                                                   Print Chart Prep Tool
                                               </Button>
                                           </div>
                                      }
                                      {patientGroup.data.map((patient, idx) =>
                                          <PatientRow
                                              patient={patient}
                                              quickListType={'SCHEDULE'}
                                              idx={idx}
                                              isPatientSelected={isPatientSelected}
                                              handleChangeCheckBox={handleChangeCheckBox}
                                              setSelectedPatient={setSelectedPatient}
                                              setShowPatientDetails={setShowPatientDetails}
                                              showPatientDetails={showPatientDetails}
                                              key={patient.id ?? patient.patientId ?? `${groupKey}-${idx}`}
                                              onAction={openAction}
                                          />
                                      )}
                                  </Col>
                              </Row>
                            ) : (
                                <Row id={`${componentName}-patientsSection-${index}`} className="mb-5">
                                    <Col>
                                        <div className="d-flex flex-column align-items-start mb-2">
                                            <small id={`${componentName}-MonthandDay-${index}`}>{formatDateWithPattern(patientGroup.date, "MMMM d")}</small>
                                            <h5 className="mb-0">
                                                <span id={`${componentName}-DayoftheWeek-${index}`} className="me-2">{formatDate(patientGroup.date)}</span>
                                                <Badge pill id={`${componentName}-PatientCount-${index}`} className="bg-primary">
                                                    {patientGroup.totalSize.toLocaleString()}
                                                </Badge>
                                            </h5>
                                        </div>

                                        <Card>
                                            <Card.Body>
                                                <Card.Text id={`${componentName}-NoPatients`}>
                                                    No Patients Found
                                                </Card.Text>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                              )
                            }
                          </React.Fragment>
                        );
                        }
                    )
                }
                {["schedule", "reschedule", "unschedule"].includes(activeAction) && (
                    <ScheduledAppointment
                        show={true}
                        onHide={closeAction}
                        patient={selectedPatient}
                        appointmentAction={activeAction}
                        onSuccess={async () => {
                            if (typeof handleFilterDatesResult === "function") {
                                await handleFilterDatesResult(pageNumber);
                            }
                        }}
                    />
                )}

                {activeAction === "noshow" && (
                    <NoShowModal
                        show={showNoShowModal}
                        onHide={closeAction}
                        onSubmit={handleSubmitNoShow}
                        patient={selectedPatient}
                        visits={noShowVisits}
                        selectedCareGapIds={noShowSelectedCareGapIds}
                        updateSelection={updateNoShowSelection}
                        loading={noShowLoading}
                        submitting={noShowSubmitting}
                        error={noShowError}
                        submitError={noShowSubmitError}
                    />
                )}

                <Modals
                    centered={true}
                    show={confirmationMessage}
                    scrollable={true}
                    closeConfirmation={() => setConfirmationMessage(false)}
                    onCreate={() => closeEvidenceModalMain()}
                    onHide={() => setConfirmationMessage(false)}
                    size="sm"
                    title=""
                    titleid={`${componentName}-CloseWithoutSavingModal`}
                    closeBtn="No"
                    closeBtnId={`${componentName}-closeNo`}
                    completeBtn="Yes"
                    completeBtnId={`${componentName}-completeYes`}
                    hideFooter={false}
                    body="Do you want to close without saving?"
                />
                <Modals
                    scrollable={true}
                    show={showRecentVisitModal}
                    closeSubmitModal={() => setShowRecentVisitModal(false)}
                    onHide={() => {
                        setShowRecentVisitModal(false);
                        updateParent();
                    }}
                    size="xl"
                    title="Update Last Wellness Visit"
                    titleid={`${componentName}-SubmitRecentVisitModal`}
                    completeBtn="Submit"
                    completeBtnId={`${componentName}-completeRecentVisitSubmit`}
                    hideFooter={true}
                    body={
                        <SubmitRecentVisitBody
                            patient={selectedPatient}
                            updateParent={updateParent}
                            closeSubmitModal={() => setShowRecentVisitModal(false)}
                        />
                    }
                />
            </Col>
        </Row>
    );
};

export default ScheduleListView;
